import { exhaustOp, rxMutation } from '@angular-architects/ngrx-toolkit';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { tapResponse } from '@ngrx/operators';
import { AssetStateMessage } from '@r1-compass-library/models';
import { LifecycleRuleset } from '@r1-compass-library/models/lifecycle-rules/lifecycle-ruleset.model';
import { LifecycleRulesetService } from '@r1-compass-library/services/lifecycle-ruleset.service';
import { AssetStore } from '@r1-compass-library/store/asset.store';
import { exhaustMap, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

export interface LifecycleRulesetState {
    lifecycleRuleset: LifecycleRuleset | undefined;
    isLoading: boolean;
    error: string | undefined;
    message: AssetStateMessage | undefined;
}

export const initialState: LifecycleRulesetState = {
    lifecycleRuleset: undefined,
    isLoading: false,
    error: undefined,
    message: undefined,
};

@Injectable({
    providedIn: 'root',
})
export class LifecycleRulesetStore extends AssetStore<LifecycleRuleset, LifecycleRulesetState> {
    readonly #lifecycleRulesetService = inject(LifecycleRulesetService);
    protected readonly route = inject(ActivatedRoute);

    lifecycleRuleset$ = this.select(state => state.lifecycleRuleset);
    lifecycleRuleset = this.selectSignal(state => state.lifecycleRuleset);
    message$ = this.select(state => state.message);

    assetTypeName = 'Lifecycle Ruleset';

    constructor() {
        super(initialState);
    }

    reset = this.updater(state => ({
        ...initialState,
    }));

    setIsLoading = this.updater(state => ({
        ...state,
        isLoading: true,
    }));

    setLifecycleRuleset = this.updater((state, lifecycleRuleset: LifecycleRuleset) => ({
        ...state,
        lifecycleRuleset: lifecycleRuleset,
        isLoading: false,
    }));

    setError = this.updater((state, error: HttpErrorResponse) => ({
        ...state,
        isLoading: false,
        error: error.message,
    }));

    setMessage = this.updater((state, message: AssetStateMessage) => ({
        ...state,
        isLoading: false,
        message: message,
    }));

    resetMessage = this.updater(state => ({
        ...state,
        isLoading: false,
        message: undefined,
    }));

    getLifecycleRuleset = this.effect((id$: Observable<number>) => {
        return id$.pipe(
            tap(() => this.setIsLoading()),
            exhaustMap(id => {
                const userDivisionCodes: string[] = this.usersAvailableDivisions().map(
                    availableDivision => availableDivision.divisionCode
                );
                return this.#lifecycleRulesetService.search({ id: id, divisions: userDivisionCodes }).pipe(
                    tapResponse({
                        next: lifecycleRulesets =>
                            this.handleSearchResponse(lifecycleRulesets, this.setLifecycleRuleset, this.setMessage),
                        error: (err: HttpErrorResponse) => console.error(err),
                    })
                );
            })
        );
    });

    saveLifecycleRuleset = rxMutation({
        operation: (lifecycleRuleset: Partial<LifecycleRuleset>) => {
            const serviceCall: Observable<LifecycleRuleset> = lifecycleRuleset.id
                ? this.#lifecycleRulesetService.update(lifecycleRuleset.id, lifecycleRuleset)
                : this.#lifecycleRulesetService.create(lifecycleRuleset);
            return serviceCall;
        },
        operator: exhaustOp,
        onSuccess: ruleset => {
            this.setLifecycleRuleset(ruleset);
        },
        onError: err => {
            console.error(err);
        },
    });
}
